/**
 * Fixed-timestep game loop.
 *
 * Physics runs at exactly `fixedDt` no matter what the display does, so the
 * bounce height is identical on a 60 Hz laptop and a 240 Hz monitor. Rendering
 * happens once per animation frame with an `alpha` in `[0, 1)` describing how
 * far between the last two substeps the frame sits — `PogoBody.getPose`
 * interpolates on it, which is what removes the 120 Hz stair-step from the
 * camera.
 *
 * Pause-safe: a frame delta is clamped to `maxSubsteps · fixedDt` before it ever
 * reaches the accumulator, so returning to a backgrounded tab does not simulate
 * the minutes it was away (the classic "spiral of death", and in this game also
 * a free teleport through the level).
 */

export class FixedLoop {
  private readonly fixedDt: number;
  private readonly maxSubsteps: number;
  private readonly fixedUpdate: (dt: number) => void;
  private readonly render: (alpha: number, timeSec: number) => void;

  private raf = 0;
  private running = false;
  private lastMs = 0;
  private acc = 0;
  private simTime = 0;

  /** Substeps run in the most recent frame — the debug overlay reads this. */
  steps = 0;
  /** Smoothed frames per second. */
  fps = 0;

  constructor(
    fixedDt: number,
    maxSubsteps: number,
    fixedUpdate: (dt: number) => void,
    render: (alpha: number, timeSec: number) => void,
  ) {
    this.fixedDt = fixedDt;
    this.maxSubsteps = Math.max(1, Math.floor(maxSubsteps));
    this.fixedUpdate = fixedUpdate;
    this.render = render;
  }

  /** Monotonic simulated seconds since `start`. Animation phase is keyed off this. */
  get timeSec(): number {
    return this.simTime + this.acc;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastMs = performance.now();
    this.acc = 0;
    document.addEventListener('visibilitychange', this.onVisibility);
    this.raf = requestAnimationFrame(this.frame);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** Coming back from a hidden tab: drop the gap rather than trying to catch up. */
  private readonly onVisibility = (): void => {
    if (!document.hidden) {
      this.lastMs = performance.now();
      this.acc = 0;
    }
  };

  private readonly frame = (nowMs: number): void => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.frame);

    let dt = (nowMs - this.lastMs) / 1000;
    this.lastMs = nowMs;
    if (!(dt > 0)) dt = 0;

    const budget = this.fixedDt * this.maxSubsteps;
    if (dt > budget) dt = budget;
    this.fps += (1 / Math.max(dt, 1e-4) - this.fps) * 0.1;

    this.acc += dt;
    if (this.acc > budget) this.acc = budget;

    let steps = 0;
    while (this.acc >= this.fixedDt && steps < this.maxSubsteps) {
      this.fixedUpdate(this.fixedDt);
      this.acc -= this.fixedDt;
      this.simTime += this.fixedDt;
      steps++;
    }
    this.steps = steps;

    this.render(this.acc / this.fixedDt, this.simTime + this.acc);
  };
}
